import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Connection,
  SupplyPeriodSummary,
  ChargeComponent,
  ChargeTag,
  useUpdateSupplyperiodV3,
  SupplyPeriodDetails,
} from '../connectionsApi';
import AuthorizedAction from '../../common/AuthorizedAction';
import Alert, { AlertEnum } from '../../layout/Alert';
import { Permission } from '../../../auth/getPermissions';
import {
  EuiBasicTable,
  EuiButton,
  EuiCheckbox,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSelect,
} from '@elastic/eui';

interface ExpectedChargeComponentsFormProps {
  connection: Connection;
  supplyPeriod: SupplyPeriodSummary;
  supplyPeriodV3: SupplyPeriodDetails;
  onCancel: () => void;
  onSubmit: () => void;
}

const ExpectedChargeComponentsForm: FunctionComponent<ExpectedChargeComponentsFormProps> = ({
  supplyPeriod,
  supplyPeriodV3,
  onCancel,
  onSubmit,
}) => {
  const allChargeTags: ChargeTag[] = ['NETWORK', 'ENERGY', 'EQUIPMENT', 'LEVY', 'ENERGYFEE'];
  const { error, isLoading, mutateAsync } = useUpdateSupplyperiodV3(supplyPeriod.id, {
    enabled: !!supplyPeriod?.id,
  });

  const [chargeComponents, setChargeComponents] = useState<ChargeComponent[]>([]);

  useEffect(() => {
    supplyPeriodV3.expectedChargeComponents &&
      setChargeComponents(supplyPeriodV3.expectedChargeComponents);
  }, [supplyPeriodV3]);

  function handleSubmit() {
    mutateAsync({
      ...supplyPeriodV3,
      expectedChargeComponents: chargeComponents,
    });
    supplyPeriodV3.expectedChargeComponents = chargeComponents;
    onSubmit();
  }

  return (
    <>
      <>
        <EuiModalHeader>
          <EuiModalHeaderTitle>Edit charge components</EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiModalBody>
          <>
            {error && <Alert type={AlertEnum.DANGER}>Sorry, there was an error</Alert>}
            {!isLoading && (
              <EuiBasicTable
                items={allChargeTags.map((tag) => {
                  return {
                    tag: tag,
                    flow: chargeComponents.find((component) => component.tag == tag)?.flow ?? 'IN',
                  };
                })}
                columns={[
                  {
                    field: 'tag',
                    name: 'Charge Tag',
                    render: (tag: any) => (
                      <EuiCheckbox
                        id={tag}
                        label={
                          tag === 'ENERGYFEE'
                            ? 'Energy fee'
                            : tag.charAt(0) + tag.slice(1).toLowerCase()
                        }
                        checked={!!chargeComponents.find((component) => component.tag == tag)}
                        onChange={() => {
                          if (chargeComponents.find((component) => component.tag == tag)) {
                            setChargeComponents(
                              chargeComponents.filter((component) => component.tag != tag)
                            );
                          } else {
                            setChargeComponents([...chargeComponents, { tag: tag, flow: 'IN' }]);
                          }
                        }}
                      />
                    ),
                  },
                  {
                    field: 'flow',
                    name: 'Flow Direction',
                    render: (flow: string, item: ChargeComponent) => (
                      <EuiSelect
                        id="tag"
                        options={[
                          { text: 'Into Connection', value: 'IN', selected: flow == 'IN' },
                          { text: 'Out Of Connection', value: 'OUT', selected: flow == 'OUT' },
                          { text: 'Both Directions', value: 'BOTH', selected: flow == 'BOTH' },
                        ]}
                        disabled={
                          !chargeComponents.map((component) => component.tag).includes(item.tag)
                        }
                        onChange={(newValue) => {
                          if (chargeComponents.find((component) => component.tag == item.tag)) {
                            const newChargeComponent: ChargeComponent = {
                              tag: item.tag,
                              flow: newValue.target.value as 'IN' | 'OUT' | 'BOTH',
                            };
                            setChargeComponents([
                              ...chargeComponents.filter(
                                (component) => !(component.tag == item.tag)
                              ),
                              newChargeComponent,
                            ]);
                          }
                        }}
                      />
                    ),
                  },
                ]}
              />
            )}
          </>
        </EuiModalBody>
        <EuiModalFooter>
          <EuiButton
            data-testid="cancel-button"
            className="apl-button-v1"
            onClick={() => onCancel()}
            disabled={isLoading}
            type="button"
          >
            Cancel
          </EuiButton>
          <AuthorizedAction
            extraClasses="is-primary"
            isDisabled={
              isLoading ||
              chargeComponents == supplyPeriodV3.expectedChargeComponents ||
              supplyPeriodV3.expectedChargeComponents == undefined
            }
            permission={Permission.CONNECTION_EDIT}
            testId="save-connection-button"
            type="submit"
            onClick={handleSubmit}
          >
            Save
          </AuthorizedAction>
        </EuiModalFooter>
      </>
    </>
  );
};

export default ExpectedChargeComponentsForm;
